import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '../../../Button'
import { getEmptyStateActions, PageEmptyState } from '../../../PageEmptyState'
import { Table } from '../../../Table'
import { TablePagination } from '../../../TablePagination'
import Text from '../../../Text'
import {
  useViewingDocumentUpload,
  useViewingProfileContext,
} from '../RentalPortalViewingProfile.hooks'

import {
  QA_NAME_VIEWING_PROFILE_DOCUMENTS_RETRY,
  QA_NAME_VIEWING_PROFILE_DOCUMENTS_TABLE,
  QA_NAME_VIEWING_PROFILE_DOCUMENTS_UPLOAD,
  VIEWING_DOCUMENT_COLUMNS_CONFIG,
  VIEWING_DOCUMENT_UPLOAD_ACTION_NAME,
  VIEWING_PROFILE_DOCUMENTS_SKELETON_ROWS,
} from './RentalPortalViewingProfileDocuments.constants'
import { useViewingProfileDocuments } from './RentalPortalViewingProfileDocuments.hooks'
import { getIsViewingDocumentsEmpty } from './RentalPortalViewingProfileDocuments.utils'
import { ViewingProfileDocumentsRow } from './ViewingProfileDocumentsRow'

import * as styles from './RentalPortalViewingProfileDocuments.module.css'

import DocumentIcon from '../../../../icons/document.svg'
import RefreshIcon from '../../../../icons/refresh.svg'
import RentalPortalIcon from '../../../../icons/rental-portal.svg'
import UploadIcon from '../../../../icons/upload-1.svg'

// TODO: replace with the shared `EntityTabDocuments` (RP-02 §337) once the
// backend serves the listing `directory_id` — wire it like
// `FlatProfileTabDocuments`; this mock table then goes away
function RentalPortalViewingProfileDocuments() {
  const { t } = useTranslation()

  const { documentsReloadKey, viewingData } = useViewingProfileContext()

  const { handleOpenDocumentUpload } = useViewingDocumentUpload()

  const containerRef = useRef(null)

  const {
    handleRefresh,
    handleSetPagination,
    hasError,
    isInitiated,
    isLoading,
    items,
    pagination,
  } = useViewingProfileDocuments({
    documentsReloadKey,
    viewingId: viewingData?.id,
  })

  const canUpload = viewingData?.permissions?.can_manage

  return (
    <div
      className={styles.RentalPortalViewingProfileDocuments}
      ref={containerRef}
    >
      {hasError ? (
        <PageEmptyState EntityIcon={RentalPortalIcon}>
          <Text isSecondary>
            {t('RentalPortal.ViewingProfileDocumentsLoadErrorKey')}
          </Text>
          <Button.Flat
            beforeIconProps={{ Icon: RefreshIcon }}
            isLoading={isLoading}
            qaName={QA_NAME_VIEWING_PROFILE_DOCUMENTS_RETRY}
            text={t('RentalPortal.ListingRetryBtnKey')}
            onClick={handleRefresh}
          />
        </PageEmptyState>
      ) : getIsViewingDocumentsEmpty({ documents: items, isInitiated }) ? (
        <PageEmptyState
          EntityIcon={DocumentIcon}
          actions={getEmptyStateActions({
            Icon: UploadIcon,
            actionName: VIEWING_DOCUMENT_UPLOAD_ACTION_NAME,
            qaName: QA_NAME_VIEWING_PROFILE_DOCUMENTS_UPLOAD,
            shouldShowActions: canUpload,
            text: t('RentalPortal.ViewingDocumentUploadBtnKey'),
            onClick: handleOpenDocumentUpload,
          })}
          text={t('RentalPortal.ViewingProfileDocumentsEmptyKey')}
        />
      ) : (
        <>
          {canUpload ? (
            <div className={styles.toolbar}>
              <Button.Text
                beforeIconProps={{ Icon: UploadIcon }}
                qaName={QA_NAME_VIEWING_PROFILE_DOCUMENTS_UPLOAD}
                text={t('RentalPortal.ViewingDocumentUploadBtnKey')}
                onClick={handleOpenDocumentUpload}
              />
            </div>
          ) : null}
          <Table
            TableRow={ViewingProfileDocumentsRow}
            actionsColumnWidth={0}
            columns={VIEWING_DOCUMENT_COLUMNS_CONFIG}
            containerRef={containerRef}
            context={QA_NAME_VIEWING_PROFILE_DOCUMENTS_TABLE}
            headerRowProps={{ resizable: false }}
            isLoading={isLoading}
            items={items}
            skeletonProps={{ withCheckbox: false }}
            skeletonRows={VIEWING_PROFILE_DOCUMENTS_SKELETON_ROWS}
          />
          <TablePagination
            responsive
            containerRef={containerRef}
            isSkeleton={!isInitiated}
            value={pagination}
            onChange={handleSetPagination}
          />
        </>
      )}
    </div>
  )
}

export default RentalPortalViewingProfileDocuments
